import { useState } from 'react';
import { AlertTriangle, UserPlus } from 'lucide-react';
import type { SalesManager, SalesTeam } from '../../types/salesTeamConfig';

interface UnassignedManagersListProps {
    managers: SalesManager[];
    teams: SalesTeam[];
    onAssign: (salesManagerId: string, salesTeamId: number) => Promise<void>;
}

export function UnassignedManagersList({ managers, teams, onAssign }: UnassignedManagersListProps) {
    const [selectedTeams, setSelectedTeams] = useState<Record<string, number>>({});
    const [assigningId, setAssigningId] = useState<string | null>(null);

    if (managers.length === 0) return null;

    const handleAssign = async (managerId: string) => {
        const teamId = selectedTeams[managerId];
        if (!teamId) return;

        setAssigningId(managerId);
        try {
            await onAssign(managerId, teamId);
            setSelectedTeams(prev => {
                const next = { ...prev };
                delete next[managerId];
                return next;
            });
        } finally {
            setAssigningId(null);
        }
    };

    return (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h2 className="text-sm font-semibold text-amber-800">
                    Unassigned Managers ({managers.length})
                </h2>
            </div>
            <div className="divide-y divide-amber-100">
                {managers.map(manager => (
                    <div
                        key={manager.SalesManagerID}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm"
                    >
                        <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900">
                                {manager.SalesManagerName}
                            </span>
                            <span className="ml-2 text-gray-500">
                                {manager.SalesManagerCode}
                            </span>
                        </div>
                        <span className="text-xs text-gray-500 uppercase shrink-0">
                            {manager.CompanyCode}
                        </span>
                        <span className="text-xs text-gray-400 truncate max-w-[180px] hidden sm:inline">
                            {manager.SalesManagerEmail}
                        </span>
                        <select
                            value={selectedTeams[manager.SalesManagerID] ?? ''}
                            onChange={e =>
                                setSelectedTeams(prev => ({
                                    ...prev,
                                    [manager.SalesManagerID]: Number(e.target.value),
                                }))
                            }
                            className="rounded border border-gray-300 py-1 px-2 text-sm focus:border-blue-500 focus:outline-none"
                        >
                            <option value="">Select team...</option>
                            {teams.map(t => (
                                <option key={t.SalesTeamID} value={t.SalesTeamID}>
                                    {t.SalesTeamName}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => handleAssign(manager.SalesManagerID)}
                            disabled={!selectedTeams[manager.SalesManagerID] || assigningId === manager.SalesManagerID}
                            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <UserPlus className="h-3.5 w-3.5" />
                            Assign
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
